/**
 * 📍 재고 보관 장소 — 장소별 센 수 입력 (v2.226.0 · 2026-10-02 · 사용자 요청)
 *
 * 사용자: 「재고보관장소가 2군데인데, 10층 공장과 B1 창고 — 재고를 셀 때 수량 넣는 칸을 두 개로 나눠서 해줘.
 *   일부 입력했더라도 그 상태로 나중에로 다음 차례로 넘어갈 수 있게」 · 분기 실사 + 주간 확인 · 「한 칸이라도 적어야 저장 · 빈 칸은 0」.
 *
 * ⭐장소 목록은 **서버가 내려 준다**(backend/src/lib/stock/locations.ts 한 곳). 여기는 칸 글자 ↔ 숫자 다루기만.
 * ⭐키가 없음(아직 안 셈) ≠ 0(세어 보니 없음) — 「나중에」 일부 입력은 센 장소만 보낸다.
 * ⭐맞음·다름은 **서버가 합계로 판정**한다. 화면의 판정 글자(✅/⚠)는 미리보기일 뿐.
 */

export interface StockLocation {
  key: string;
  label: string;
}

/** 장소별 센 수 — 센 장소만 키가 있다 */
export type LocQty = Record<string, number>;
/** 입력 칸 글자 (장소 key → 글자) */
export type LocDraft = Record<string, string>;

/** 수량 칸 — 비었거나 0 이상 정수(서버 상한 1,000,000 안) */
const QTY_RE = /^\d{1,6}$/;

export function draftFrom(q: LocQty | null | undefined, locations: readonly StockLocation[]): LocDraft {
  const d: LocDraft = {};
  for (const l of locations) d[l.key] = q && typeof q[l.key] === 'number' ? String(q[l.key]) : '';
  return d;
}

export interface ParsedDraft {
  /** 잘못 적은 칸의 장소 이름(없으면 null) */
  bad: string | null;
  /** 센 장소만 */
  qty: LocQty;
  /** 한 칸이라도 적었나 */
  any: boolean;
  /** 합계 — 빈 칸은 0 */
  sum: number;
}

export function parseDraft(d: LocDraft, locations: readonly StockLocation[]): ParsedDraft {
  const qty: LocQty = {};
  let bad: string | null = null;
  let sum = 0;
  for (const l of locations) {
    const t = (d[l.key] ?? '').trim();
    if (t === '') continue;
    if (!QTY_RE.test(t)) { bad = bad ?? l.label; continue; }
    qty[l.key] = Number(t);
    sum += Number(t);
  }
  return { bad, qty, any: Object.keys(qty).length > 0, sum };
}

/** 두 입력이 같은가(적던 것이 바뀌었나 판단용) */
export function sameDraft(a: LocDraft, b: LocDraft): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if ((a[k] ?? '').trim() !== (b[k] ?? '').trim()) return false;
  return true;
}

/** 「10층 공장 45 · B1 창고 15」 (센 장소만) */
export function locText(q: LocQty | null | undefined, locations: readonly StockLocation[]): string {
  if (!q) return '';
  return locations.filter((l) => typeof q[l.key] === 'number')
    .map((l) => `${l.label} ${q[l.key]!.toLocaleString('ko-KR')}`).join(' · ');
}
